
import { ScrapedArticle } from "@/types";
import { Component, Vue } from "vue-property-decorator";
import * as ScraperServices from "@/services/ScraperService";
import { formatRelative } from "date-fns";
import { nlBE } from "date-fns/locale";
import { ROOT_ACTIONS } from "@/store/actions";

interface ArticleEditForm {
  title: string;
  description: string;
  author: string;
  conclusion: string;
  thumbnailUrl: string;
  claimReviewed: string;
  rating: string;
  claimant: string;
  sourceUrl: string;
  claimDate: string;
  reviewDate: string;
}

const emptyEditForm = (): ArticleEditForm => ({
  title: "",
  description: "",
  author: "",
  conclusion: "",
  thumbnailUrl: "",
  claimReviewed: "",
  rating: "",
  claimant: "",
  sourceUrl: "",
  claimDate: "",
  reviewDate: "",
});

@Component
export default class RTBFTool extends Vue {
  scrapedArticles: ScrapedArticle[] = [];
  selectedScrapedArticle: ScrapedArticle | null = null;
  editForm: ArticleEditForm = emptyEditForm();
  loading = false;

  async loadArticles() {
    const { rows } = await ScraperServices.GetScrapedArticles(
      1000,
      0,
      "RTBF",
      ["date"],
      [true]
    );
    this.scrapedArticles = rows;
  }

  async mounted() {
    await this.loadArticles();
  }

  formatDate(d: string): string {
    return formatRelative(new Date(d), new Date(), {
      locale: nlBE,
    });
  }

  toDateInput(value: string | null | undefined): string {
    if (!value) return "";
    return new Date(value).toISOString().substring(0, 10);
  }

  parseClaimReviewData(data: unknown): Record<string, any> {
    if (!data) return {};
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch {
        return {};
      }
    }
    return { ...(data as Record<string, any>) };
  }

  getPropertyValue(article: ScrapedArticle, key: string): string {
    const prop = article.ScrapedArticleProperties?.find((p) => p.key === key);
    return prop?.value ?? "";
  }

  getClaimReviewFieldValue(cr: Record<string, any>, key: string): string {
    const fromClaim = cr.claim?.[key]?.value;
    if (fromClaim !== undefined && fromClaim !== null) {
      return String(fromClaim);
    }

    const fromMetadata = cr.article_metadata?.[key]?.value;
    if (fromMetadata !== undefined && fromMetadata !== null) {
      return String(fromMetadata);
    }

    return "";
  }

  populateEditForm(article: ScrapedArticle) {
    const properties = article.ScrapedArticleProperties ?? [];

    if (article.version === 2 && properties.length > 0) {
      this.editForm = {
        title: article.title || "",
        description:
          this.getPropertyValue(article, "introduction") || article.description || "",
        author: article.author || this.getPropertyValue(article, "author") || "",
        conclusion:
          this.getPropertyValue(article, "conclusion") || article.conclusion || "",
        thumbnailUrl: article.thumbnailUrl || "",
        claimReviewed: this.getPropertyValue(article, "claimReviewed"),
        rating:
          this.getPropertyValue(article, "textualRating") ||
          this.getPropertyValue(article, "verdict"),
        claimant: this.getPropertyValue(article, "claimants"),
        sourceUrl: this.getPropertyValue(article, "source"),
        claimDate: this.toDateInput(this.getPropertyValue(article, "dateClaimMade")),
        reviewDate: this.toDateInput(
          this.getPropertyValue(article, "datePublished") || article.date
        ),
      };
      return;
    }

    const cr = this.parseClaimReviewData(article.claimReviewData);

    if (cr.article_metadata || cr.claim) {
      this.editForm = {
        title: article.title || "",
        description:
          this.getClaimReviewFieldValue(cr, "introduction") || article.description || "",
        author: article.author || this.getClaimReviewFieldValue(cr, "author") || "",
        conclusion:
          this.getClaimReviewFieldValue(cr, "conclusion") || article.conclusion || "",
        thumbnailUrl: article.thumbnailUrl || "",
        claimReviewed: this.getClaimReviewFieldValue(cr, "claimReviewed"),
        rating:
          this.getClaimReviewFieldValue(cr, "textualRating") ||
          this.getClaimReviewFieldValue(cr, "verdict"),
        claimant: this.getClaimReviewFieldValue(cr, "claimants"),
        sourceUrl: this.getClaimReviewFieldValue(cr, "source"),
        claimDate: this.toDateInput(this.getClaimReviewFieldValue(cr, "dateClaimMade")),
        reviewDate: this.toDateInput(
          this.getClaimReviewFieldValue(cr, "datePublished") || article.date
        ),
      };
      return;
    }

    this.editForm = {
      title: article.title || "",
      description: article.description || "",
      author: article.author || "",
      conclusion: article.conclusion || "",
      thumbnailUrl: article.thumbnailUrl || "",
      claimReviewed: cr.claimReviewed || "",
      rating: cr.reviewRating?.alternateName || "",
      claimant: cr.itemReviewed?.author?.name || "",
      sourceUrl: cr.itemReviewed?.appearance?.[0]?.url || "",
      claimDate: this.toDateInput(cr.itemReviewed?.datePublished),
      reviewDate: this.toDateInput(cr.datePublished),
    };
  }

  buildClaimReviewDataFromProperties(
    existing: unknown,
    form: ArticleEditForm
  ): Record<string, any> {
    const cr = this.parseClaimReviewData(existing);

    if (!cr.article_metadata) {
      cr.article_metadata = {};
    }
    if (!cr.claim) {
      cr.claim = {};
    }

    const setField = (section: "article_metadata" | "claim", key: string, value: string) => {
      if (!cr[section][key]) {
        cr[section][key] = {};
      }
      cr[section][key].value = value || null;
    };

    setField("article_metadata", "introduction", form.description);
    setField("article_metadata", "datePublished", form.reviewDate);
    setField("claim", "claimReviewed", form.claimReviewed);
    setField("claim", "textualRating", form.rating);
    setField("claim", "claimants", form.claimant);
    setField("claim", "source", form.sourceUrl);
    setField("claim", "dateClaimMade", form.claimDate);
    setField("claim", "conclusion", form.conclusion);

    return cr;
  }

  buildClaimReviewData(existing: unknown, form: ArticleEditForm): Record<string, any> {
    const cr = this.parseClaimReviewData(existing);

    if (cr.article_metadata || cr.claim) {
      return this.buildClaimReviewDataFromProperties(existing, form);
    }

    cr.claimReviewed = form.claimReviewed;

    if (!cr.reviewRating) {
      cr.reviewRating = { "@type": "Rating" };
    }
    cr.reviewRating.alternateName = form.rating;

    if (!cr.itemReviewed) {
      cr.itemReviewed = { "@type": "Claim" };
    }
    if (!cr.itemReviewed.author) {
      cr.itemReviewed.author = { "@type": "Person" };
    }
    cr.itemReviewed.author.name = form.claimant;

    if (!cr.itemReviewed.appearance) {
      cr.itemReviewed.appearance = [{ "@type": "CreativeWork" }];
    }
    if (!cr.itemReviewed.appearance[0]) {
      cr.itemReviewed.appearance[0] = { "@type": "CreativeWork" };
    }
    cr.itemReviewed.appearance[0].url = form.sourceUrl;
    cr.itemReviewed.datePublished = form.claimDate || null;

    cr.datePublished = form.reviewDate || null;

    return cr;
  }

  async selectArticle(article: ScrapedArticle) {
    this.selectedScrapedArticle = await ScraperServices.GetScrapedArticle(article.id);
    this.populateEditForm(this.selectedScrapedArticle);
  }

  async saveChanges() {
    if (!this.selectedScrapedArticle) return;

    try {
      this.loading = true;

      const claimReviewData = this.buildClaimReviewData(
        this.selectedScrapedArticle.claimReviewData,
        this.editForm
      );

      await ScraperServices.UpdateScrapedArticle(this.selectedScrapedArticle.id, {
        title: this.editForm.title,
        description: this.editForm.description,
        author: this.editForm.author,
        conclusion: this.editForm.conclusion,
        thumbnailUrl: this.editForm.thumbnailUrl,
        claimReviewData,
        approved: true,
      });

      const propertyUpdates: Record<string, string> = {
        introduction: this.editForm.description,
        claimReviewed: this.editForm.claimReviewed,
        textualRating: this.editForm.rating,
        claimants: this.editForm.claimant,
        source: this.editForm.sourceUrl,
        dateClaimMade: this.editForm.claimDate,
        datePublished: this.editForm.reviewDate,
        conclusion: this.editForm.conclusion,
      };

      for (const prop of this.selectedScrapedArticle.ScrapedArticleProperties ?? []) {
        if (propertyUpdates[prop.key] === undefined) continue;

        await ScraperServices.UpdateScrapedArticleProperty(
          this.selectedScrapedArticle.id,
          prop.id,
          propertyUpdates[prop.key]
        );
      }

      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "success",
        text: "Wijzigingen opgeslagen",
      });
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: "Kon wijzigingen niet opslaan",
      });
    } finally {
      await this.loadArticles();
      this.loading = false;
    }
  }
}
